<template>
  <!--
    玩家情报卡：街道「玩家列表」点人后弹出。

    只放**公开信息**（昵称 / 称号 / 等级 / 上站次数 / 简介 / 形象），
    不放属性明细和熟练度——那是玩家自己的事，摆到别人眼前只会催生比较。
  -->
  <el-dialog
    custom-class="pc-dialog"
    :visible.sync="visible"
    :show-close="false"
    width="340px"
    append-to-body>
    <div class="pc-card">
      <div class="pc-bar">
        <span class="pc-bar-title">玩家情报</span>
        <span class="pc-close" role="button" aria-label="关闭" @click="close">×</span>
      </div>

      <div class="pc-main">
        <div class="pc-portrait">
          <div class="pc-face">
            <div class="pc-layer" :style="layer('faceUrl')"></div>
            <div class="pc-layer" :style="layer('eyeUrl')"></div>
            <div class="pc-layer" :style="layer('mouthUrl')"></div>
            <div class="pc-layer" :style="layer('hairUrl')"></div>
          </div>
        </div>

        <div class="pc-ident">
          <div class="pc-nick">{{ nickName }}</div>
          <div class="pc-title-line">
            <TitleBadge
              v-if="titleInfo && titleInfo.title"
              :title="titleInfo.title"
              :short-title="titleInfo.shortTitle"
              :tier="titleInfo.tier"
              :type-name="titleInfo.typeName"
              :evaluate-date="titleInfo.evaluateDate"
              variant="plain"
              ground="light"
              :max-chars="14" />
            <span v-else class="pc-none">尚未获得称号</span>
          </div>
          <div class="pc-meta">
            <span v-if="loading">读取中…</span>
            <template v-else>
              <span v-if="levelText">{{ levelText }}</span>
              <span v-if="levelText && loginText" class="pc-sep">·</span>
              <span v-if="loginText">{{ loginText }}</span>
            </template>
          </div>
        </div>
      </div>

      <div class="pc-remark" :class="{ 'pc-remark-empty': !remark }">{{ remark || '这个人什么也没有写。' }}</div>

      <div class="pc-actions" v-if="!isSelf">
        <button class="pc-btn pc-btn-primary" :disabled="!userName" @click="whisper">私 聊</button>
        <button class="pc-btn" :disabled="!targetId" @click="addFriend">加好友</button>
      </div>
      <div class="pc-self" v-else>这是你自己的情报</div>
    </div>
  </el-dialog>
</template>

<script>
import { getRequest } from '../../utils/api'
import Message from 'element-ui/lib/message'
import TitleBadge from './TitleBadge'

export default {
  name: 'PlayerCard',
  components: { TitleBadge },
  props: {
    /** 当前登录玩家的 id，用来把「点到自己」和「点到别人」区分开 */
    selfId: { type: [String, Number], default: '' }
  },
  data () {
    return {
      visible: false,
      loading: false,
      userName: '',
      nickName: '',
      targetId: '',
      titleInfo: null,
      fantasyUser: {},
      fantasyUserDetail: {},
      header: {}
    }
  },
  computed: {
    isSelf () {
      return !!this.selfId && String(this.selfId) === String(this.targetId)
    },
    remark () {
      return this.fantasyUser.remark || ''
    },
    levelText () {
      const lv = this.fantasyUserDetail.level
      return lv != null ? '等级 ' + lv : ''
    },
    loginText () {
      const n = this.fantasyUserDetail.logintimes
      return n != null ? '上站 ' + n + ' 次' : ''
    }
  },
  methods: {
    /**
     * @param {Object} user      玩家列表条目（Redis 快照），至少含 userName / nickName
     * @param {Object} titleInfo 该玩家称号（由列表页批量接口带下来，避免二次请求）
     */
    open (user, titleInfo) {
      if (!user) return
      this.titleInfo = titleInfo || null
      this.userName = user.userName || ''
      this.nickName = user.nickName || user.userName || '无名氏'
      this.targetId = user.id != null ? String(user.id) : ''
      this.fantasyUser = {}
      this.fantasyUserDetail = {}
      this.header = {}
      this.visible = true
      this.load()
    },
    close () {
      this.visible = false
    },
    load () {
      if (!this.userName) return
      this.loading = true
      getRequest('/user/user/userDetail', { userName: this.userName }).then(resp => {
        this.loading = false
        if (resp.status == 200 && resp.data && resp.data.status == 200) {
          const d = resp.data.data || {}
          this.fantasyUser = d.fantasyUser || {}
          this.fantasyUserDetail = d.fantasyUserDetail || {}
          this.header = d.userHeader || {}
          if (this.fantasyUser.id != null) this.targetId = String(this.fantasyUser.id)
          if (this.fantasyUser.nickName) this.nickName = this.fantasyUser.nickName
          // 列表页没带称号时兜底补一次（例如刚评定完、批量接口尚未刷新）
          if ((!this.titleInfo || !this.titleInfo.title) && this.targetId) this.loadTitle()
        } else {
          Message.error((resp.data && resp.data.message) || '查询玩家情报失败')
        }
      }, () => {
        this.loading = false
        Message.error('查询玩家情报异常')
      })
    },
    loadTitle () {
      getRequest('/user/title/getUserTitle', { userId: this.targetId }).then(resp => {
        if (resp.status == 200 && resp.data && 200 == resp.data.status && resp.data.data) {
          this.titleInfo = resp.data.data
        }
      }, () => {})
    },
    layer (key) {
      const url = this.header && this.header[key]
      return url ? { backgroundImage: 'url(' + this.ossPath + url + ')' } : {}
    },
    whisper () {
      if (!this.userName) return
      // 移动端聊天挂在侧滑抽屉里（默认收起）：先把抽屉拉出来再填私聊对象，
      // 否则填进的是看不见的输入框，提示「已填入」但玩家找不到
      if (window.innerWidth <= 768) {
        this.$root.eventHub.$emit('openDrawer')
      }
      // 交给聊天区：填入私聊对象并把光标放进输入框（桌面/移动端都挂载着 chat）
      this.$root.eventHub.$emit('whisperTo', this.userName)
      Message.success('已填入私聊对象：' + this.nickName)
      this.close()
    },
    addFriend () {
      if (!this.targetId) return
      this.$root.eventHub.$emit('addFriend', { id: this.targetId, name: this.nickName })
      this.close()
    }
  }
}
</script>

<style>
/* el-dialog 内部节点在 scoped 作用域之外，这里只能用普通样式，靠 .pc-dialog 前缀隔离 */
.pc-dialog.el-dialog {
  padding: 0;
  border: 1px solid #0080ff;
  border-radius: 3px;
  background: #fdfdf5;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(20, 55, 85, 0.35);
}
.pc-dialog .el-dialog__header {
  display: none;
}
.pc-dialog .el-dialog__body {
  padding: 0;
  color: #333;
}

.pc-bar {
  position: relative;
  height: 26px;
  line-height: 26px;
  background: #0080ff;
  text-align: center;
}
.pc-bar-title {
  font-size: 14px;
  font-weight: bold;
  color: #fff;
  letter-spacing: 5px;
  /* 抵消末字的字距，视觉才真正居中 */
  margin-right: -5px;
}
.pc-close {
  position: absolute;
  top: 0;
  right: 0;
  width: 26px;
  height: 26px;
  line-height: 24px;
  font-size: 18px;
  color: #fff;
  cursor: pointer;
  text-align: center;
}
.pc-close:hover {
  background: #0069d9;
}

.pc-main {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px 8px;
}
.pc-portrait {
  flex-shrink: 0;
  width: 52px;
  height: 64px;
  margin-right: 10px;
  background: #e8dfcf;
  border: 2px solid #8b7355;
  border-radius: 3px;
  overflow: hidden;
}
/* 形象素材是 64x80 四层叠图，按 0.8 缩放塞进 52x64 的框里 */
.pc-face {
  position: relative;
  width: 64px;
  height: 80px;
  transform: scale(0.8);
  transform-origin: top left;
}
.pc-layer {
  position: absolute;
  left: 0;
  top: 0;
  width: 64px;
  height: 80px;
  background-size: 64px 80px;
  background-repeat: no-repeat;
}
.pc-ident {
  flex: 1;
  min-width: 0;
}
.pc-nick {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  line-height: 22px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pc-title-line {
  line-height: 20px;
  min-height: 20px;
  margin: 1px 0 2px;
}
.pc-none {
  font-size: 12px;
  color: #999;
}
.pc-meta {
  font-size: 12px;
  color: #777;
  line-height: 16px;
}
.pc-sep {
  margin: 0 5px;
  color: #bbb;
}

.pc-remark {
  margin: 0 12px 10px;
  padding-top: 8px;
  border-top: 1px dashed #dfe8f2;
  font-size: 12px;
  line-height: 18px;
  color: #555;
  max-height: 54px;
  overflow: hidden;
  word-break: break-all;
}
.pc-remark-empty {
  color: #aaa;
}

.pc-actions {
  display: flex;
  gap: 8px;
  padding: 0 12px 12px;
}
.pc-btn {
  flex: 1;
  height: 30px;
  font-family: Microsoft YaHei;
  font-size: 13px;
  color: #0080ff;
  background: #fff;
  border: 1px solid #0080ff;
  border-radius: 2px;
  cursor: pointer;
}
.pc-btn-primary {
  color: #fff;
  background: #0080ff;
}
.pc-btn:hover:not(:disabled) {
  background: #0069d9;
  border-color: #0069d9;
  color: #fff;
}
.pc-btn:disabled {
  color: #b8b8b8;
  border-color: #d5d5d5;
  background: #f5f5f5;
  cursor: not-allowed;
}

.pc-self {
  padding: 0 12px 12px;
  font-size: 12px;
  color: #999;
  text-align: center;
  line-height: 18px;
}

@media (max-width: 768px) {
  .pc-dialog.el-dialog {
    width: 88% !important;
    margin-top: 12vh !important;
  }
  .pc-actions .pc-btn {
    height: 34px;
  }
}
</style>
